import { Injectable } from '@angular/core';
import { AuthService, LogoutOptions } from '@auth0/auth0-angular';
import {
  BehaviorSubject,
  Observable,
  Subscription,
  combineLatest,
  defaultIfEmpty,
  filter,
  firstValueFrom,
  from,
  map,
  of,
  shareReplay,
  switchMap,
  take,
  tap,
  finalize,
  catchError,
  throwError,
  timeout,
} from 'rxjs';
import jwt_decode from 'jwt-decode';
import { Logger } from '@sondermind/datadog-logging';
import { ConfigurationService } from '@sondermind/configuration';
import { SegmentSchema, SegmentService } from '@sondermind/segment';
import { AmplitudeFeatureFlagService } from '@sondermind/amplitude';

// Errors that should NOT trigger logout - these are benign errors during initialization or user actions
const AUTH0_SAFE_ERRORS = [
  'Invalid state', // User hit back button, refreshed callback URL, or bookmarked callback
];
// Errors related to token refresh that should be handled by the barrier pattern, not by immediate logout.
// When a heartbeat is active with a still-valid cached token, these errors are recoverable.
const AUTH0_REFRESH_ERRORS = [
  'Missing Refresh Token',
  'invalid refresh token',
  'Lock not available', // Web Locks API returns null lock in browser-level edge cases (tab discard, memory pressure)
];
// Legacy constants for monitoring/logging
const AUTH0_ACCESS_DENIED_ERRORS = ['access_denied', 'hs_context_invalid'];
const HEADSPACE_CONTEXT_CLAIM = 'https://www.headspace.com/context';
// Paths that should be preserved in appState when redirecting to Auth0 login
const DEEPLINK_REDIRECT_PATHS = ['/therapy-journey', '/messaging'];
interface AppState {
  target: string;
}
interface ITokenInfo {
  sub: string;
  meta: {
    authsvc_account_id: string;
  };
}
type IdTokenClaims = Record<string, unknown>;

@Injectable({
  providedIn: 'root',
})
export class Auth0TokenService {
  auth0TokenBehaviorSubject$ = new BehaviorSubject<string>('');
  private auth0EnabledBehaviorSubject$ = new BehaviorSubject<boolean>(false);
  private isFirstLoginFromAuth0Signup = new BehaviorSubject<boolean>(false);
  private heartbeatTimeoutId: ReturnType<typeof setTimeout> | null = null;

  /**
   * Shared observable for token refresh - ensures only one refresh operation happens at a time.
   * When multiple callers need a token simultaneously, they all share the same in-flight request.
   */
  private refreshInProgress$: Observable<string> | null = null;

  /**
   * Timestamp of the last successful token refresh (via forceRefresh).
   * Used to prevent rapid successive refresh calls when multiple 401 error handlers fire sequentially.
   */
  private lastForceRefreshTimestamp = 0;

  /** Minimum interval (ms) between forced refresh token exchanges to prevent authsvc spikes. */
  private static readonly forceRefreshCooldownMs = 5_000;

  /** Minimum heartbeat delay (ms) to prevent tight refresh loops when tokens are near expiry. */
  private static readonly minHeartbeatDelayMs = 30_000;

  /**
   * Tracks consecutive heartbeat refreshes that resolved to the minimum delay.
   * Used to detect and break tight refresh loops where the Auth0 SDK keeps returning
   * near-expiry tokens despite force-refresh requests.
   */
  private consecutiveMinDelayRefreshes = 0;

  /** After this many consecutive min-delay refreshes, back off to the fallback interval. */
  private static readonly maxConsecutiveMinDelayRefreshes = 2;

  heartbeatSubscription: Subscription | null = null;
  auth0Enabled$ = this.auth0EnabledBehaviorSubject$.asObservable();
  auth0Token$ = this.auth0TokenBehaviorSubject$.asObservable();
  authSuccess$ = new BehaviorSubject<boolean>(false);
  isFirstLoginFromAuth0Signup$ =
    this.isFirstLoginFromAuth0Signup.asObservable();
  constructor(
    private auth0: AuthService,
    private logger: Logger,
    private configService: ConfigurationService,
    private segmentService: SegmentService<SegmentSchema>,
    private amplitudeFeatureFlagService: AmplitudeFeatureFlagService,
  ) {
    this.auth0.error$.subscribe((err: any) => {
      const isSafeError = AUTH0_SAFE_ERRORS.includes(err.message);

      this.logger.error(`Auth0 error: ${err.message}`, {
        errorType: err.message,
        isAccessDenied: AUTH0_ACCESS_DENIED_ERRORS.includes(err.message),
        error: err.error
      });
      // Handle safe errors that don't require logout
      if (isSafeError) {
        this.logger.info(`Safe error detected (${err.message}) - not logging out`);
        // For "Invalid state", redirect to home to clear the callback URL
        // BUT: Don't redirect if we're on the calendar OAuth callback route
        // (Google Calendar OAuth uses 'code' param which Auth0 mistakes for its own callback)
        if (err.message === 'Invalid state') {
          const currentPath = window.location.pathname;
          const isCalendarOAuth = currentPath.includes('calendar_external_authorization');

          if (isCalendarOAuth) {
            this.logger.info('Ignoring Auth0 "Invalid state" error - on calendar OAuth callback route');
            return;
          }

          this.logger.info('Redirecting to home due to Auth0 "Invalid state" error');
          window.location.href = '/';
        }
        return;
      }

      // If it's a refresh error and we have an active heartbeat with a valid cached token,
      // let the barrier pattern handle recovery instead of immediately logging out.
      // This prevents users from being kicked out of video sessions when the Auth0 SDK
      // loses the refresh token from its in-memory cache (e.g., due to browser memory pressure).
      const isRefreshError = AUTH0_REFRESH_ERRORS.some((e) => err.message?.includes(e));
      if (isRefreshError && this.heartbeatTimeoutId) {
        const currentToken = this.auth0TokenBehaviorSubject$.getValue();
        if (currentToken && this.isTokenStillValid(currentToken)) {
          this.logger.info(
            `Refresh error during active heartbeat with valid token - deferring to barrier pattern (${err.message})`
          );
          return;
        }
      }

      this.logout();
    });
  }

  get auth0Enabled(): boolean {
    return this.auth0EnabledBehaviorSubject$.getValue();
  }

  initializeAuth0(): Observable<string | null> {
    // Log entry point with current URL state for debugging SSO issues
    this.logger.debug('initializeAuth0 starting', {
      pathname: window.location.pathname,
      search: window.location.search,
      hasSsoInFlight: sessionStorage.getItem('sso_in_flight') === '1'
    });

    return combineLatest([
      this.auth0.isLoading$,
      this.auth0.isAuthenticated$,
    ]).pipe(
      filter(([isLoading]) => !isLoading),
      map(([, isAuthenticated]) => isAuthenticated),
      tap((isAuthenticated) => {
        // Log Auth0 SDK state after loading completes
        this.logger.debug('Auth0 SDK ready', {
          isAuthenticated,
        });
      }),
      switchMap((isAuthenticated) => {
        if (!isAuthenticated) {
          const isSsoInFlight = sessionStorage.getItem('sso_in_flight') === '1';
          const hasSSOParams =
            window.location.search.includes('sso=') ||
            window.location.search.includes('error=login_required');

          if (isSsoInFlight && hasSSOParams) {
            this.logger.debug('SSO flow in progress, waiting for Auth0 callback to complete', {
              isSsoInFlight,
              hasSSOParams,
            });
            return of(null as string | null);
          }
          const hasAuth0Error = window.location.search.includes('error=');
          if (hasAuth0Error) {
            this.logger.error('Auth0 returned an error, logging out to prevent redirect loop');
            return this.logout().pipe(map(() => null as string | null));
          }
          // not logged in yet → kick off redirect, emit null
          this.logger.info('User not authenticated, redirecting to Auth0 login', {
            isSsoInFlight,
            hasSSOParams,
          });
          const loginOptions = this.buildLoginOptions();

          return from(
            this.auth0.loginWithRedirect(loginOptions)
          ).pipe(map(() => null as string | null));
        }
        // user is authenticated → get token but don't trigger refresh
        this.logger.debug('User authenticated, fetching access token silently');
        // Use cacheMode: 'on' to avoid triggering refresh token flow
        return from(
          firstValueFrom(
            this.auth0.getAccessTokenSilently({
              cacheMode: 'on',
              authorizationParams: {
                scope: 'offline_access',
                eeNamespace: this.configService.env.eeNamespace,
              },
            })
          )
        );
      }),
      switchMap(async(token) => {
        if (!token) {
          this.logger.debug('No token received - user not authenticated or redirect in progress');
        }
        if (token) {
          this.logger.debug('Access token received, processing authentication');
          this.auth0TokenBehaviorSubject$.next(token);
          const decoded: ITokenInfo = jwt_decode(token);

          // Identify user in analytics services - wrapped in try-catch to prevent blocking auth
          try {
            this.segmentService.identifyUser(decoded.meta.authsvc_account_id);
            this.amplitudeFeatureFlagService.setUserId(decoded.meta.authsvc_account_id);
          } catch (analyticsError: unknown) {
            const errorMessage = analyticsError instanceof Error ? analyticsError.message : String(analyticsError);
            this.logger.warn('Failed to identify user in analytics services', { error: errorMessage });
          }

          try {
            // Check for appState redirect first (e.g., from /therapy-journey)
            // appState$ may not emit if there's no appState, so we use timeout and defaultIfEmpty
            const appState = await firstValueFrom<AppState | null>(
              this.auth0.appState$.pipe(
                timeout(100), // Short timeout to avoid waiting if no appState
                filter((state) => state !== null && state !== undefined),
                take(1),
                defaultIfEmpty(null)
              )
            ).catch((): AppState | null => null);

            if (appState?.target) {
              this.logger.info(`Redirecting to appState target: ${appState.target}`);
              window.location.href = appState.target;
              return null;
            }

            const hasProcessedClaims =
              sessionStorage.getItem('hs_redirected_to');

            if (!hasProcessedClaims) {
              this.logger.debug('Checking for Headspace redirect intent in ID token claims');
              const idToken = await firstValueFrom<IdTokenClaims | null>(
                this.auth0.idTokenClaims$.pipe(
                  timeout(500), // Add reasonable timeout for claims
                  filter((claims) => claims !== null && claims !== undefined),
                  take(1),
                  defaultIfEmpty(null)
                )
              ).catch((error: unknown): IdTokenClaims | null => {
                const errorMessage = error instanceof Error ? error.message : String(error);
                this.logger.warn('ID token claims timeout or error', { error: errorMessage });
                return null;
              });

              // This should be just a path something like /dashboard
              const headspaceRedirectIntent =
                idToken?.[HEADSPACE_CONTEXT_CLAIM] as string | undefined;

              if (headspaceRedirectIntent) {
                if (this.isValidRedirectPath(headspaceRedirectIntent)) {
                  this.logger.info('Headspace redirect intent found, redirecting', {
                    redirectPath: headspaceRedirectIntent
                  });
                  sessionStorage.setItem(
                    'hs_redirected_to',
                    headspaceRedirectIntent
                  );
                  window.location.href = headspaceRedirectIntent;
                  return null;
                }
                this.logger.warn('Headspace redirect intent found but path is invalid', {
                  redirectPath: headspaceRedirectIntent
                });
              } else {
                this.logger.debug('No Headspace redirect intent found in ID token claims');
              }
            }
          } catch (error) {
            this.logger.error(`Failed to get ID token claims: ${error}`);
          }
        }
        this.auth0EnabledBehaviorSubject$.next(true);
        this.logger.info('Auth0 initialization complete', {
          hasToken: !!token,
          auth0Enabled: true
        });
        return token;
      })
    );
  }

  setAuth0Success(): void {
    this.authSuccess$.next(true);
  }

  getAuth0Success(): boolean {
    return this.authSuccess$.getValue();
  }

  /**
   * Gets an access token using a barrier pattern to prevent concurrent refresh token exchanges.
   * If a refresh is already in progress, all callers will share the same request.
   * This prevents the "Token could not be decoded or is missing in DB" error caused by
   * multiple processes trying to use the same refresh token simultaneously.
   *
   * Phase 1: Uses Auth0 SDK as the single source of truth while maintaining BehaviorSubject
   * for backward compatibility. The BehaviorSubject is synced with Auth0 SDK's cache.
   * Controlled by USE_AUTH0_FIX feature flag.
   *
   * @param forceRefresh - If true, bypasses the cache and forces a new token fetch.
   *                       Use sparingly as this always triggers a refresh token exchange.
   * @returns Observable that emits the access token
   */
  getTokenWithBarrier(forceRefresh: boolean = false): Observable<string> {
    // PHASE 1: If feature flag enabled, use Auth0 SDK as single source of truth
    if (!forceRefresh) {
      return from(
        this.auth0.getAccessTokenSilently({
          cacheMode: 'on', // Only check cache, let Auth0 decide if it needs refresh
          authorizationParams: {
            scope: 'offline_access',
            eeNamespace: this.configService.env.eeNamespace,
          },
          timeoutInSeconds: 10,
        })
      ).pipe(
        tap((token) => {
          // PHASE 1: Sync our BehaviorSubject with Auth0 SDK's cache
          this.auth0TokenBehaviorSubject$.next(token);
          this.logger.debug('Token retrieved from Auth0 cache and synced to BehaviorSubject');
        }),
        catchError((error: unknown) => {
          const errorMessage = error instanceof Error ? error.message : String(error);

          // Auth0 cache miss or token expired - PHASE 1: Clear our cache too
          this.auth0TokenBehaviorSubject$.next('');
          this.logger.debug('Auth0 cache miss, cleared BehaviorSubject, will attempt refresh', {
            error: errorMessage
          });

          // Fall through to refresh logic below by throwing
          return throwError(() => error);
        }),
        // If successful cache hit, return immediately
        // If cache lookup failed, fall through to the refresh barrier
        catchError(() => this.performTokenRefresh(forceRefresh))
      );
    }
    // If forceRefresh is requested but we just refreshed within the cooldown window,
    // check BehaviorSubject (which should be synced with Auth0) and return if still valid.
    // This prevents N sequential 401 error handlers from each triggering a separate network call.
    if (forceRefresh) {
      const msSinceLastRefresh = Date.now() - this.lastForceRefreshTimestamp;
      if (msSinceLastRefresh < Auth0TokenService.forceRefreshCooldownMs) {
        const currentToken = this.auth0TokenBehaviorSubject$.getValue();
        if (currentToken && this.isTokenStillValid(currentToken)) {
          this.logger.debug(
            'Force refresh requested but cooldown active, returning recent token',
            { msSinceLastRefresh }
          );
          return of(currentToken);
        }
      }
    }

    // Perform the actual token refresh
    return this.performTokenRefresh(forceRefresh);
  }

  /**
   * PHASE 1 HELPER: Performs the actual token refresh with barrier pattern.
   * Extracted to avoid code duplication between cache-miss and force-refresh paths.
   */
  private performTokenRefresh(forceRefresh: boolean): Observable<string> {
    // If a refresh is already in progress, return the shared observable
    if (this.refreshInProgress$) {
      this.logger.debug('Token refresh already in progress, sharing existing request');
      return this.refreshInProgress$;
    }

    this.logger.info('Starting token refresh with barrier', { forceRefresh });

    // Create a new shared refresh observable
    this.refreshInProgress$ = from(
      this.auth0.getAccessTokenSilently({
        cacheMode: 'off',
        authorizationParams: {
          scope: 'offline_access',
          eeNamespace: this.configService.env.eeNamespace,
        },
        // Add timeout to prevent hanging refresh attempts
        timeoutInSeconds: 10,
      })
    ).pipe(
      tap((token) => {
        // PHASE 1: Sync BehaviorSubject with Auth0 SDK's newly refreshed token
        this.auth0TokenBehaviorSubject$.next(token);
        if (forceRefresh) {
          this.lastForceRefreshTimestamp = Date.now();
        }
        this.logger.info('Token refresh completed successfully and synced to BehaviorSubject');
      }),
      catchError((error: unknown) => {
        const errorMessage = error instanceof Error ? error.message : String(error);
        this.logger.error('Token refresh failed in barrier', { error: errorMessage });

        this.auth0TokenBehaviorSubject$.next('');

        const isRefreshTokenError =
            errorMessage.includes('Missing Refresh Token') || errorMessage.includes('invalid refresh token');

        if (isRefreshTokenError) {
          // Try cache-only fetch first -- the Auth0 SDK may still have a valid access token
          // cached even though the refresh token is lost
          this.logger.info('Refresh token unavailable, attempting cache-only token fetch');

          return from(
            this.auth0.getAccessTokenSilently({
              cacheMode: 'on',
              authorizationParams: {
                scope: 'offline_access',
                eeNamespace: this.configService.env.eeNamespace,
              },
            })
          ).pipe(
            tap((cachedToken) => {
              // PHASE 1: Sync BehaviorSubject with fallback token from Auth0 cache
              this.auth0TokenBehaviorSubject$.next(cachedToken);
              this.logger.info('Cache-only token fetch succeeded after refresh failure and synced to BehaviorSubject');
            }),
            catchError(() => {
              // Cache miss -- Auth0 SDK has no valid token
              // BehaviorSubject was already cleared above, so caches are in sync (both empty)
              this.logger.warn('Cache-only fetch also failed - no valid tokens in Auth0 SDK or BehaviorSubject');
              return throwError(() => error);
            })
          );
        }
        return throwError(() => error);
      }),
      finalize(() => {
        // Clear the in-progress observable so future requests start fresh
        this.refreshInProgress$ = null;
      }),
      // Share the same observable among all concurrent subscribers.
      // refCount: true ensures the internal subscription is cleaned up once all subscribers
      // unsubscribe, preventing leaked subscriptions that can interfere with Auth0 SDK state.
      shareReplay({ bufferSize: 1, refCount: true })
    );

    return this.refreshInProgress$;
  }

  /**
   * Checks if the token is expiring soon (within 2 minutes).
   * This helps proactively refresh tokens before they expire.
   */
  private isTokenExpiringSoon(token: string): boolean {
    try {
      const decoded = jwt_decode<{ exp: number; }>(token);
      const expiresAt = decoded.exp * 1000; // Convert to milliseconds
      const now = Date.now();
      const twoMinutesInMs = 2 * 60 * 1000;

      return expiresAt - now < twoMinutesInMs;
    } catch {
      // If we can't decode the token, consider it expiring
      return true;
    }
  }

  /**
   * Checks if the token is still technically valid (not yet expired).
   * More lenient than isTokenExpiringSoon - only checks actual expiration.
   */
  private isTokenStillValid(token: string): boolean {
    try {
      const decoded = jwt_decode<{ exp: number; }>(token);
      const expiresAt = decoded.exp * 1000; // Convert to milliseconds
      const now = Date.now();

      return expiresAt > now;
    } catch {
      // If we can't decode the token, consider it invalid
      return false;
    }
  }

  logout(
    logoutOptions: LogoutOptions = {
      logoutParams: { returnTo: window.location.origin },
    }
  ): Observable<void> {
    return this.auth0.logout(logoutOptions);
  }

  /**
   * Starts a heartbeat that proactively refreshes tokens before they expire.
   * Uses the barrier pattern to prevent race conditions with other refresh attempts.
   *
   * @param heartbeatInterval - Interval in milliseconds between refresh attempts.
   *                            Default is 14 minutes (840000ms) since access tokens expire after 15 minutes.
   */
  startHeartbeat(heartbeatInterval: number = 14 * 60 * 1000): void {
    if (!this.auth0Enabled) return;

    this.stopHeartbeat();

    const currentToken = this.auth0TokenBehaviorSubject$.getValue();
    if (currentToken) {
      this.scheduleHeartbeatRefresh(currentToken, heartbeatInterval);
      return;
    }

    this.heartbeatSubscription = this.getTokenWithBarrier().pipe(
      catchError((err: unknown) => {
        const errorMessage = err instanceof Error ? err.message : String(err);
        this.logger.error('Initial heartbeat token refresh failed:', { error: errorMessage });
        return of('');
      })
    ).subscribe((token) => {
      if (token) {
        this.scheduleHeartbeatRefresh(token, heartbeatInterval);
      }
    });
  }

  stopHeartbeat(): void {
    if (this.heartbeatTimeoutId) {
      clearTimeout(this.heartbeatTimeoutId);
      this.heartbeatTimeoutId = null;
    }
    if (this.heartbeatSubscription) {
      this.heartbeatSubscription.unsubscribe();
      this.heartbeatSubscription = null;
    }
    this.consecutiveMinDelayRefreshes = 0;
  }

  setFirstLoginFromAuth0Signup(): void {
    this.isFirstLoginFromAuth0Signup.next(true);
  }

  completeFirstTimeAuth0Signup(): void {
    this.isFirstLoginFromAuth0Signup.next(false);
  }

  private buildLoginOptions(): { authorizationParams: { scope: string; eeNamespace: string; }; appState?: AppState; } {
    // Check if user is on a path that should be preserved in appState
    const currentPath = window.location.pathname;
    const currentSearch = window.location.search;
    const shouldPreservePath = DEEPLINK_REDIRECT_PATHS.some((path) => currentPath.includes(path));

    const loginOptions: { authorizationParams: { scope: string; eeNamespace: string; }; appState?: AppState; } = {
      authorizationParams: {
        scope: 'offline_access',
        eeNamespace: this.configService.env.eeNamespace,
      },
    };

    if (shouldPreservePath) {
      const targetUrl = currentSearch
        ? `${currentPath}${currentSearch}`
        : currentPath;
      loginOptions.appState = { target: targetUrl };
    }

    return loginOptions;
  }

  private isValidRedirectPath(path: string): boolean {
    if (!path.startsWith('/')) {
      return false;
    }
    const allowedPaths = ['/dashboard', '/profile', '/appointments'];

    return allowedPaths.some((allowedPath) => path.startsWith(allowedPath));
  }

  private scheduleHeartbeatRefresh(token: string, fallbackInterval: number): void {
    if (this.heartbeatTimeoutId) {
      clearTimeout(this.heartbeatTimeoutId);
      this.heartbeatTimeoutId = null;
    }

    const { computedDelay, expiresAt } = this.getRefreshDelayMs(token, fallbackInterval);
    let refreshDelayMs = computedDelay;

    if (computedDelay <= Auth0TokenService.minHeartbeatDelayMs) {
      this.consecutiveMinDelayRefreshes += 1;
      if (this.consecutiveMinDelayRefreshes > Auth0TokenService.maxConsecutiveMinDelayRefreshes) {
        this.logger.warn(
          'Heartbeat stuck in tight refresh loop — token expiry not extending after refresh. '
          + 'Backing off to fallback interval.',
          { consecutiveMinDelayRefreshes: this.consecutiveMinDelayRefreshes, fallbackMinutes: fallbackInterval / 60000 }
        );
        refreshDelayMs = fallbackInterval;
      }
    } else {
      this.consecutiveMinDelayRefreshes = 0;
    }

    this.logger.info('Scheduling heartbeat refresh', {
      expiresAt: new Date(expiresAt * 1000),
      refreshDelayMinutes: refreshDelayMs / 60000,
      remainingMinutes: (new Date(expiresAt * 1000).getTime() - new Date().getTime()) / 60000
    });

    this.heartbeatTimeoutId = setTimeout(() => {
      this.heartbeatSubscription = this.getTokenWithBarrier(true).pipe(
        catchError((err: unknown) => {
          const errorMessage = err instanceof Error ? err.message : String(err);
          this.logger.error('Heartbeat token refresh failed:', { error: errorMessage });
          const fallbackToken = this.auth0TokenBehaviorSubject$.getValue();
          if (fallbackToken && this.isTokenStillValid(fallbackToken)) {
            this.logger.info('Heartbeat refresh failed but existing token still valid, continuing');
            return of(fallbackToken);
          }
          return of('');
        })
      ).subscribe((refreshedToken) => {
        if (refreshedToken) {
          this.logger.info('Token refreshed via heartbeat');
          this.scheduleHeartbeatRefresh(refreshedToken, fallbackInterval);
        }
      });
    }, refreshDelayMs);
  }

  private getRefreshDelayMs(token: string, fallbackInterval: number): { expiresAt: number; computedDelay: number; } {
    const oneMinuteInMs = 60 * 1000;
    let decoded = { exp: 0 };
    try {
      decoded = jwt_decode<{ exp: number; }>(token);
      const expiresAt = decoded.exp * 1000;
      const now = Date.now();
      const remainingMs = expiresAt - now;
      const refreshAt = expiresAt - oneMinuteInMs;
      const computedDelay = Math.max(refreshAt - now, Auth0TokenService.minHeartbeatDelayMs);

      if (computedDelay <= Auth0TokenService.minHeartbeatDelayMs) {
        this.logger.warn('Token near expiry at heartbeat scheduling', {
          remainingSeconds: Math.round(remainingMs / 1000),
          tokenExpiry: new Date(expiresAt).toISOString(),
        });
      }
      return { expiresAt: decoded.exp, computedDelay };
    } catch {
      this.logger.warn('Failed to decode token for heartbeat scheduling; using fallback interval');
      return {
        expiresAt: decoded.exp,
        computedDelay: Math.max(fallbackInterval - oneMinuteInMs, Auth0TokenService.minHeartbeatDelayMs) };
    }
  }
}
